import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiMessageCircle, FiUsers, FiMail, FiGlobe, FiCheck, FiSliders, FiBell, FiChevronDown, FiArrowRight } from 'react-icons/fi';
import { IoMdMegaphone } from 'react-icons/io';
import { IGlobalState } from '../../../model/IGlobalState';
import { toast } from '../Toast';
import Toggle from '../Toggle';
import { useNotifications, setRead, markAllRead, NotificationCategory, NotificationGroup, NotificationItem } from './notificationsStore';

// url segment → filter; no segment = All. Every tab's badge counts its items in the placeholder list.
const tabs: { path: string; label: string; icon?: JSX.Element; category?: NotificationCategory }[] = [
    { path: '', label: 'All' },
    { path: 'approvals', label: 'Approvals', icon: <IoMdMegaphone />, category: 'approvals' },
    { path: 'questions', label: 'Investor Questions', icon: <FiMessageCircle />, category: 'questions' },
    { path: 'registry', label: 'Registry', icon: <FiUsers />, category: 'registry' },
    { path: 'email', label: 'Email', icon: <FiMail />, category: 'email' },
    { path: 'hub', label: 'Website Hub', icon: <FiGlobe />, category: 'hub' },
];

const GROUPS: { key: NotificationGroup; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'week', label: 'Earlier this week' },
];

// "This Week" rail rows, dot colours from the spec
const SUMMARY: { label: string; category: NotificationCategory; colour: string }[] = [
    { label: 'Approvals waiting', category: 'approvals', colour: '#f59e0b' },
    { label: 'Questions open', category: 'questions', colour: '#00b5d1' },
    { label: 'Registry reports', category: 'registry', colour: '#16a34a' },
    { label: 'Email reports', category: 'email', colour: '#64748b' },
    { label: 'Security alerts', category: 'security', colour: '#dc2626' },
];

const PREVIEW = 'Preview only: this isn’t connected yet';

const Notifications: React.FC = () => {
    const history = useHistory();
    const { tab = '' } = useParams<{ tab?: string }>();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const items = useNotifications();
    const [expanded, setExpanded] = useState<string | null>(null);
    const [channels, setChannels] = useState({ digest: true, approvals: true, browser: false });
    const current = tabs.find(x => x.path === tab) ?? tabs[0];

    const visible = current.category ? items.filter(x => x.category === current.category) : items;
    const count = (category?: NotificationCategory) => category ? items.filter(x => x.category === category).length : items.length;
    const unread = items.filter(x => x.unread).length;

    const act = (n: NotificationItem) => {
        setRead(n.id);
        toast.info(`${PREVIEW}: “${n.action}” will open the related page`);
    };

    const row = (n: NotificationItem) => <div key={n.id} className={`notification${n.unread ? ' unread' : ''}`} onClick={() => setRead(n.id)}>
        <span className="dot" />
        <div className="text">
            <div className="title-row">
                <span className={`title${n.danger ? ' danger' : ''}`}>{n.title}</span>
                <span className="time">{n.time}</span>
            </div>
            <div className="body">{n.body}</div>
            {expanded === n.id && <div className="more">
                <button type="button" className="link" onClick={e => { e.stopPropagation(); setRead(n.id, n.unread); setExpanded(null); }}>
                    {n.unread ? 'Mark as read' : 'Mark as unread'}
                </button>
            </div>}
        </div>
        <button type="button" className="btn-outline" onClick={e => { e.stopPropagation(); act(n); }}>{n.action}</button>
        <button type="button" className={`expand${expanded === n.id ? ' open' : ''}`} aria-label="More"
            onClick={e => { e.stopPropagation(); setExpanded(expanded === n.id ? null : n.id); }}><FiChevronDown /></button>
    </div>;

    return <div className="v2-page notifications-v2">
        <div className="sub-tabs">
            {tabs.map(x => <button key={x.path} type="button" className={x === current ? 'active' : ''}
                onClick={() => history.push(`/notifications${x.path && `/${x.path}`}`)}>
                {x.icon} {x.label} <span className="badge">{count(x.category)}</span>
            </button>)}
        </div>

        <div className="page-header">
            <div>
                <h1>Notifications</h1>
                <p>Everything that needs your attention across {stock?.companyName || 'your clients'}, in one place</p>
            </div>
            <button type="button" className="btn-outline" onClick={() => { markAllRead(); toast.success('All notifications marked as read'); }} disabled={unread === 0}><FiCheck /> Mark All as Read</button>
            <button type="button" className="btn-outline" onClick={() => history.push('/account/notifications')}><FiSliders /> Notification Preferences</button>
        </div>

        <div className="notifications-row">
            <div className="notification-list card">
                {GROUPS.map(g => {
                    const inGroup = visible.filter(x => x.group === g.key);
                    return inGroup.length > 0 && <React.Fragment key={g.key}>
                        <div className="group-label">{g.label}</div>
                        {inGroup.map(row)}
                    </React.Fragment>;
                })}
                {visible.length === 0 && <p className="empty">No notifications here yet.</p>}
                <button type="button" className="load-older" onClick={() => toast.info(PREVIEW)}>Load older notifications</button>
            </div>

            <div className="rail">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiBell /></span>
                        <div><h2>This Week</h2><p>{items.length} notifications · {unread} unread</p></div>
                    </div>
                    {SUMMARY.map(s => <div key={s.label} className="summary-row">
                        <span className="dot" style={{ background: s.colour }} />
                        <span className="label">{s.label}</span>
                        <strong>{count(s.category)}</strong>
                    </div>)}
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiSliders /></span><div><h2>How You Get Notified</h2></div></div>
                    <div className="channel-row"><span>Email digest every morning at 8 am</span>
                        <Toggle label="Email digest" checked={channels.digest} onChange={v => setChannels(c => ({ ...c, digest: v }))} /></div>
                    <div className="channel-row"><span>Instant email for approvals</span>
                        <Toggle label="Instant email for approvals" checked={channels.approvals} onChange={v => setChannels(c => ({ ...c, approvals: v }))} /></div>
                    <div className="channel-row"><span>Browser notifications</span>
                        <Toggle label="Browser notifications" checked={channels.browser} onChange={v => setChannels(c => ({ ...c, browser: v }))} /></div>
                    <button type="button" className="link" onClick={() => history.push('/account/notifications')}>All Notification Preferences <FiArrowRight /></button>
                </section>
            </div>
        </div>
    </div>;
};

export default Notifications;
